import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import './LoginForm.css'
import CButton from '../../../components/UI/CButton'
import CTextField from '../../../components/UI/CTextField'

/**
 * Login form component
 * Handles both first-time login (username only) and standard login (username + password)
 */
const LoginForm = ({
    onSubmit,
    onForgotPassword,
}) => {
    const { isLoading } = useSelector((state) => state.auth)

    const [formData, setFormData] = useState({
        username: '',
        password: '',
    })

    const [loginFlowState, setLoginFlowState] = useState({
        isFlowSelectionVisible: true,
        isFirstLoginFlow: false,
        isStandardFlow: false,
    })

    const [formErrors, setFormErrors] = useState({})
    const [showPassword, setShowPassword] = useState(false)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }))

        // Clear field error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: '',
            }))
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (onSubmit) {
            onSubmit({
                username: formData.username.trim(),
                password: formData.password.trim(),
            })
        }
    }

    const handleForgotPasswordClick = (e) => {
        e.preventDefault()
        if (onForgotPassword) {
            onForgotPassword()
        }
    }

    return (
        <>
            {loginFlowState.isFirstLoginFlow ? (
                // First Login / Activate Account Flow
                <>
                    <div className="form-header">
                        <h2 className="form-title header-2">Let's set up your account</h2>
                        <p className="form-subtitle text-4">
                            Enter your username to get started.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-fields">
                            <CTextField
                                fullWidth
                                label="Username"
                                name="username"
                                type="text"
                                leftIcon="single-neutral-id-card"
                                value={formData.username}
                                onChange={handleChange}
                                error={!!formErrors.username}
                                helperText={formErrors.username}
                                disabled={isLoading}
                                autoComplete="username"
                                autoFocus
                            />
                        </div>

                        <CButton
                            className="submit-button"
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            showLoader={isLoading}
                            colorTheme="primary"
                            label="Continue"
                            rightIcon="arrow-right"
                            disabled={isLoading || !formData.username.trim()}
                        />

                        <div className="divider"></div>

                        <CButton
                            variant="text"
                            size="medium"
                            fullWidth
                            colorTheme="primary"
                            label="Back to login"
                            onClick={() => setLoginFlowState({
                                isFlowSelectionVisible: true,
                                isFirstLoginFlow: false,
                                isStandardFlow: false,
                            })}
                        />
                    </form>
                </>
            ) : (
                // Standard Login Flow
                <>
                    <div className="form-header">
                        <h2 className="form-title header-2">Log in to your account</h2>
                        <p className="form-subtitle text-5">
                            Enter your registered details to access your dashboard.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-fields">
                            <CTextField
                                fullWidth
                                label="Username"
                                name="username"
                                type="text"
                                leftIcon="single-neutral-id-card"
                                value={formData.username}
                                onChange={handleChange}
                                error={!!formErrors.username}
                                helperText={formErrors.username}
                                disabled={isLoading}
                                autoComplete="username"
                                autoFocus
                            />

                            <CTextField
                                fullWidth
                                label="Password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                error={!!formErrors.password}
                                helperText={formErrors.password}
                                disabled={isLoading}
                                autoComplete="current-password"
                                leftIcon="lock"
                                rightIcon={showPassword ? "hide-password" : "reveal-password"}
                                onRightIconClick={() => setShowPassword(!showPassword)}
                            />
                        </div>

                        {onForgotPassword && (
                            <p className="forgot-password-link">
                                <a href="#" onClick={handleForgotPasswordClick}>
                                    Forgot password?
                                </a>
                            </p>
                        )}

                        <CButton
                            className="submit-button"
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            showLoader={isLoading}
                            colorTheme="primary"
                            label="Log In"
                            rightIcon="arrow-right"
                            disabled={isLoading || !formData.username.trim() || !formData.password.trim()}
                        />

                        <div className="divider"></div>
                    </form>

                    <div className="activate-account-section">
                        <div className="form-header">
                            <h2 className="form-title header-3">Activate your account</h2>
                            <p className="form-subtitle text-5">
                                First time here? Set your password and access your account.
                            </p>
                        </div>

                        <CButton
                            className="submit-button"
                            variant="contained"
                            size="large"
                            fullWidth
                            colorTheme="primary"
                            label="Activate your account"
                            rightIcon="arrow-right"
                            onClick={() => setLoginFlowState({
                                isFlowSelectionVisible: false,
                                isFirstLoginFlow: true,
                                isStandardFlow: false,
                            })}
                        />
                    </div>
                </>
            )}
        </>
    )
}

LoginForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onForgotPassword: PropTypes.func,
}

export default LoginForm
